import { DOCUMENT } from '@angular/common';
import { ApplicationRef, ComponentRef, createComponent, EmbeddedViewRef, Inject, Injectable, Injector, Type } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DomHandler } from 'primeng/dom';
import { DynamicDialogConfig, DynamicDialogInjector, DynamicDialogRef } from 'primeng/dynamicdialog';
import { EditDialogComponent } from './editDialogComponent';


export interface EditDialogInstance {

    getEditForm(): FormGroup;

    onConfirm(editDialogComponent: EditDialogComponent<any>): void;

    onReject(editDialogComponent: EditDialogComponent<any>): void
  }


@Injectable()
export class EditDialogService {

  dialogComponentRefMap: Map<DynamicDialogRef, ComponentRef<EditDialogComponent<any>>> = new Map();

    constructor(private appRef: ApplicationRef, private injector: Injector, @Inject(DOCUMENT) private document: Document) {}

    public open(componentType: Type<any>, dialogConfig: EditDialogConfig): DynamicDialogRef {
        let config: DynamicDialogConfig = {
            header: dialogConfig.header,
            width: dialogConfig.width? dialogConfig.width: '500px',
            data: dialogConfig.data,
            contentStyle: { overflow: 'auto' },
        };
        if (!this.duplicationPermission(componentType, config)) {
            return null;
        }

        const dialogRef = this.appendDialogComponentToBody(config, dialogConfig);

        this.dialogComponentRefMap.get(dialogRef).instance.childComponentType = componentType;

        return dialogRef;
    }

    private appendDialogComponentToBody(config: DynamicDialogConfig, dialogConfig: EditDialogConfig): DynamicDialogRef {
        const map = new WeakMap();
        map.set(DynamicDialogConfig, config);

        const dialogRef = new DynamicDialogRef();
        map.set(DynamicDialogRef, dialogRef);

        const sub = dialogRef.onClose.subscribe(() => {
            this.dialogComponentRefMap.get(dialogRef).instance.close();
        });

        const destroySub = dialogRef.onDestroy.subscribe(() => {
            this.removeDialogComponentFromBody(dialogRef);
            destroySub.unsubscribe();
            sub.unsubscribe();
        });

        const componentRef = createComponent(EditDialogComponent, { environmentInjector: this.appRef.injector, elementInjector: new DynamicDialogInjector(this.injector, map) });
        componentRef.instance.confirmLabel = dialogConfig.confirmLabel;
        if(dialogConfig.additionalClass){
            componentRef.instance.setAdditionalClass(dialogConfig.additionalClass);
        }
        this.appRef.attachView(componentRef.hostView);

        const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
        if (!config.appendTo || config.appendTo === 'body') {
            this.document.body.appendChild(domElem);
        } else {
            DomHandler.appendChild(domElem, config.appendTo);
        }

        this.dialogComponentRefMap.set(dialogRef, componentRef);

        return dialogRef;
    }

    private removeDialogComponentFromBody(dialogRef: DynamicDialogRef) {
        if (!dialogRef || !this.dialogComponentRefMap.has(dialogRef)) {
            return;
        }

        const dialogComponentRef = this.dialogComponentRefMap.get(dialogRef);
        this.appRef.detachView(dialogComponentRef.hostView);
        dialogComponentRef.destroy();
        this.dialogComponentRefMap.delete(dialogRef);
    }

    private duplicationPermission(componentType: Type<any>, config: DynamicDialogConfig): boolean {
        if (config.duplicate) {
            return true;
        }
        let permission = true;
        for (const [key, value] of this.dialogComponentRefMap) {
            if (value.instance.childComponentType === componentType) {
                permission = false;
                break;
            }
        }
        return permission;
    }

}

export interface EditDialogConfig {

    header?: string;
    width?:string;
    data?: any;

    confirmLabel?: string;

    additionalClass?: string;
}
